// @flow
import * as React from 'react';
import { featureIsEnabled } from '@ta/common.features';
import { localize } from '@ta/common.i18n';
import { connect } from 'react-redux';
import { InteractionLogger } from '@ta/tracking.interactions';
import trackEventAJAX from '@ta/common.tracking';
import { LabeledIconAction } from '@ta/public.listing';
import { Opener } from '@ta/overlays.managers';
import { Icon } from '@ta/styleguide.icon';
import { getPid } from './ContentCollectionUtility';
import styles from './ActionsBlock.less';

/**
 * Props that are passed in.
 */
type ExternalProps = {|
  /** To see if user has the right permission to post medias. */
  permissions: any,
  /** To see if data is still loading. */
  permissionsLoading: boolean,
  /** To set the trigeer data to render the uploader modal outside. */
  setUploaderTriggerDataMethod: (name: string, pid: number) => void,
  /** gate function for logging in */
  loginGate: (deferredPID?: number) => Promise<void>,
|};

type Props = {|
  ...ExternalProps,
  /** To see if user is logged in */
  isLoggedIn?: boolean,
  /** Servlet name of the page. */
  pageName: string,
  /**  Location id of the page. */
  locationId: number,
  /**  If the member is signed in. */
  isMemberSignedIn: boolean,
|};

/**
 * UrlAction is used for 'Write Review', NavOverlayAction is used for the other three media uploaders.
 */
type BaseAction = {|
  label: string,
  icon: string,
  trackingName?: string,
  pid?: number,
  onClick?: (SyntheticEvent<*>) => void,
  currentLocationid: number,
  socialLoginGate: (deferredPID?: number) => Promise<void>,
  ifMemberSignedIn: boolean,
  currentServletName: string,
|};

type UrlAction = {|
  ...BaseAction,
  url: string,
|};

type NavOverlayAction = {|
  ...BaseAction,
  name: string,
  setUploaderTriggerData: (name: string, pid: number) => void,
|};

type NavUgcAction = BaseAction | UrlAction | NavOverlayAction;

type ActionProps = {|
  actions: NavUgcAction[],
|};

const NavUgcActionListing = (props: NavUgcAction) => {
  const {
    label,
    icon,
    trackingName = undefined,
    onClick,
    pid = -1,
    currentLocationid = undefined,
    socialLoginGate,
    ifMemberSignedIn,
    currentServletName,
  } = props;
  const labelPart = <span className={styles.itemLabel}>{label}</span>;
  const iconPart = <Icon className={styles.itemIcon} name={icon} />;
  /** If Overlay Action */
  if (props.name) {
    const { name, setUploaderTriggerData } = props;
    return (
      <Opener name={name}>
        {open => (
          <InteractionLogger>
            {logClick => {
              const openAndClick = event => {
                // set tripgger data then outside render function will know which modal to render.
                setUploaderTriggerData(name, pid);
                if (trackingName !== undefined) {
                  logClick(event, 'postContentCtaClick', {
                    action: trackingName,
                    entrypoint: 'GLOBAL_NAV',
                    isMemberSignedIn: ifMemberSignedIn,
                    locationId: currentLocationid,
                  });
                  trackEventAJAX({
                    module: 'postContentCtaClick',
                    action: trackingName,
                    context: `GLOBAL_NAV|${currentServletName}`,
                    isMemberSignedIn: ifMemberSignedIn,
                    value: pid,
                  });
                }
                socialLoginGate(pid).then(() => {
                  open();
                  if (onClick) {
                    onClick(event);
                  }
                });
              };
              return <LabeledIconAction icon={iconPart} label={labelPart} onClick={openAndClick} />;
            }}
          </InteractionLogger>
        )}
      </Opener>
    );
  }
  /** If Url Action */
  if (props.url) {
    const { url } = props;
    return (
      <InteractionLogger>
        {logClick => {
          const gatedTrackedOnClick = (event: SyntheticMouseEvent<*>) => {
            if (trackingName !== undefined) {
              logClick(event, 'postContentCtaClick', {
                action: trackingName,
                entrypoint: 'GLOBAL_NAV',
                locationId: currentLocationid,
              });
              trackEventAJAX({
                module: 'postContentCtaClick',
                action: trackingName,
                context: `GLOBAL_NAV|${currentServletName}`,
                isMemberSignedIn: ifMemberSignedIn,
                value: pid,
              });
            }
            event.preventDefault();
            socialLoginGate(pid).then(() => {
              if (onClick) {
                onClick(event);
              }
              // eslint-disable-next-line no-restricted-globals
              window.location.href = url;
            });
            /**
             *  In order to pass the pid to t_userreview when we post a review though clicking this Write Review button,
             *  we should set the pid in the cookie like all the others did, then we can get it from the session in backend.
             */
            global.require(['trjs!ta/util/Cookie'], Cookie => {
              Cookie.setPIDCookie(pid);
            });
          };
          return (
            <LabeledIconAction
              icon={iconPart}
              label={labelPart}
              url={url}
              onClick={gatedTrackedOnClick}
            />
          );
        }}
      </InteractionLogger>
    );
  }
  return null;
};

const NavUgcActionSection = ({ actions }: ActionProps) => (
  <div className={styles.buttons}>
    {actions.map(action => (
      <NavUgcActionListing {...action} key={action.label} />
    ))}
  </div>
);

/**
    This ActionsBlock Component includes 4 media elements which will be used as a Popover under "Post" button in global nav bar.
 */
const ActionsBlock = ({
  permissions,
  permissionsLoading,
  setUploaderTriggerDataMethod,
  isLoggedIn,
  pageName,
  locationId,
  loginGate,
  isMemberSignedIn,
}: Props) => {
  const LinkUploaderModalName = 'link-uploader-nav';
  const VideoUploaderModalName = 'video-uploader-nav';
  const PhotoUploaderModalName = 'photo-uploader-nav';
  const linkPostingFeatureEnabled = featureIsEnabled('social_link_posting_enabled');
  const videoFeatureEnabled = featureIsEnabled('media_video_upload_enabled');
  const photoFeatureEnabled = featureIsEnabled('media_photo_upload_enabled');
  const actions: NavUgcAction[] = [];
  const servletBasedPid = getPid(pageName);

  if (videoFeatureEnabled && isLoggedIn && permissions?.canUploadVideo) {
    actions.push({
      label: localize('ugc_uploader_video_v3'),
      icon: 'video-fill',
      trackingName: 'POST_VIDEOS',
      name: VideoUploaderModalName,
      setUploaderTriggerData: setUploaderTriggerDataMethod,
      currentServletName: pageName,
      pid: servletBasedPid,
      currentLocationid: locationId,
      socialLoginGate: loginGate,
      ifMemberSignedIn: isMemberSignedIn,
    });
  }

  if (photoFeatureEnabled && (!isLoggedIn || permissionsLoading || permissions?.canPostPhoto)) {
    actions.push({
      label: localize('ugc_uploader_photo_v3'),
      icon: 'camera-fill',
      name: PhotoUploaderModalName,
      trackingName: 'POST_PHOTOS',
      setUploaderTriggerData: setUploaderTriggerDataMethod,
      currentServletName: pageName,
      pid: servletBasedPid,
      currentLocationid: locationId,
      socialLoginGate: loginGate,
      ifMemberSignedIn: isMemberSignedIn,
    });
  }

  if (!isLoggedIn || permissionsLoading || permissions?.canWriteReview) {
    actions.push({
      label: localize('ugc_uploader_review_v2'), // Write review
      icon: 'pencil-paper',
      trackingName: 'WRITE_REVIEW',
      url: '/UserReview',
      pid: servletBasedPid,
      currentLocationid: locationId,
      socialLoginGate: loginGate,
      ifMemberSignedIn: isMemberSignedIn,
      currentServletName: pageName,
    });
  }

  if (linkPostingFeatureEnabled && isLoggedIn && permissions?.canPostLink) {
    actions.push({
      label: localize('ugc_uploader_linkPost_v3'), // Post links
      icon: 'link',
      name: LinkUploaderModalName,
      trackingName: 'POST_LINKS',
      setUploaderTriggerData: setUploaderTriggerDataMethod,
      currentServletName: pageName,
      pid: servletBasedPid,
      currentLocationid: locationId,
      socialLoginGate: loginGate,
      ifMemberSignedIn: isMemberSignedIn,
    });
  }

  return <NavUgcActionSection actions={actions} />;
};

ActionsBlock.defaultProps = {
  isLoggedIn: false,
};

/**
 * This Action Block is used for Global Nav CTA. It may contain 'Post photos' , 'Post videos' , 'Write Reviews', 'Post Links'(depends on the user permission or login/logout state).
 */
export default connect<Props, ExternalProps, _, _, _, _>(redux => ({
  isLoggedIn: !!redux.auth.loggedInUserId,
  pageName: redux.route.page,
  locationId: redux.route.detail,
  isMemberSignedIn: redux.auth.isMember,
}))(ActionsBlock);
